// `_TypedDataEncoder` lives under `ethers.utils` in ethers v5 — using
// the namespaced import keeps us aligned with the rest of the web app.
import { ethers } from 'ethers'
import {
  DAO_EIP712_DOMAIN_NAME,
  DAO_EIP712_DOMAIN_VERSION,
  DAO_EIP712_VERIFYING_CONTRACT,
  DAO_EIP712_VOTE_TYPES,
  DAO_SIGNED_MESSAGE_VERSION_V1,
  DaoVotePayload,
  VoteChoice
} from '@mprolab/sdk'

const VOTE_PRIMARY_TYPE = 'Vote'

export const DAO_VOTE_SIGNED_MESSAGE_VERSION = DAO_SIGNED_MESSAGE_VERSION_V1

/**
 * EIP-712 sign flow for off-chain DAO votes. The wallet provider
 * (wagmi -> walletConnect / metamask / etc.) takes the typed-data
 * structure and returns a 65-byte signature; the backend re-derives
 * the same digest from the payload and verifies the signer matches
 * the JWT-authenticated wallet.
 *
 * Backend reads `castAt` from the request body and the server-side
 * digest must match what the wallet signed — so the frontend pins
 * `castAt` at sign time and forwards it verbatim.
 */
export function buildDaoVoteTypedData(payload: DaoVotePayload, chainId: number) {
  return {
    domain: {
      name: DAO_EIP712_DOMAIN_NAME,
      version: DAO_EIP712_DOMAIN_VERSION,
      chainId,
      verifyingContract: DAO_EIP712_VERIFYING_CONTRACT as `0x${string}`
    },
    types: DAO_EIP712_VOTE_TYPES,
    primaryType: VOTE_PRIMARY_TYPE,
    message: payload
  }
}

/** Server-equivalent digest we send back so the backend can fail fast on a malformed signature. */
export function deriveDaoVoteDigest(
  payload: DaoVotePayload,
  chainId: number
): `0x${string}` {
  const domain = buildDaoVoteTypedData(payload, chainId).domain
  const digest = ethers.utils._TypedDataEncoder.hash(
    domain as unknown as Record<string, unknown>,
    DAO_EIP712_VOTE_TYPES as unknown as Record<
      string,
      Array<{ name: string; type: string }>
    >,
    payload as unknown as Record<string, unknown>
  )
  return digest as `0x${string}`
}

export function buildVotePayload(args: {
  proposalId: string
  choice: VoteChoice
  snapshotBlock: number
  voterWallet: string
}): DaoVotePayload {
  return {
    proposalId: args.proposalId,
    choice: args.choice,
    snapshotBlock: args.snapshotBlock,
    voterWallet: args.voterWallet.toLowerCase(),
    castAt: Math.floor(Date.now() / 1000)
  }
}
